import { useCallback, useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { prefersReducedMotion, reducedMotionVariant, slideDownVariants } from '@/animations';
import { Button } from '@/components';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { Icon } from '@/utils/icons';

const ONLINE_TOAST_DURATION = 3000;

const successVariants = reducedMotionVariant({
  initial: { opacity: 0, y: -10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.25, ease: [0.4, 0, 0.6, 1] },
  },
});

const OfflineBanner: React.FC = () => {
  const isOnline = useOnlineStatus();
  const [isRetrying, setIsRetrying] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showOnlineToast, setShowOnlineToast] = useState(false);

  const wasOffline = useRef(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOnline) {
      wasOffline.current = true;
    } else if (wasOffline.current) {
      wasOffline.current = false;
      setShowOnlineToast(true);

      toastTimer.current = setTimeout(() => {
        setShowOnlineToast(false);
      }, ONLINE_TOAST_DURATION);
    }

    const timer = setTimeout(() => {
      setIsDismissed(false);
    }, 0);

    return () => {
      clearTimeout(timer);
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, [isOnline]);

  const handleRetry = useCallback(() => {
    if (isRetrying) return;
    setIsRetrying(true);

    fetch('/favicon.ico', { method: 'HEAD', cache: 'no-store' })
      .catch(() => {
        // Ignored: connection check failed
      })
      .finally(() => {
        setTimeout(() => setIsRetrying(false), 1500);
      });
  }, [isRetrying]);

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  const isReduced = prefersReducedMotion();

  return (
    <AnimatePresence>
      {!isOnline && !isDismissed && (
        <motion.div
          key="offline-banner"
          variants={isReduced ? undefined : slideDownVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          role="alert"
          aria-live="assertive"
          className="from-warning-500 to-warning-600 dark:from-warning-600 dark:to-warning-700 sticky top-0 right-0 left-0 z-[9999] bg-gradient-to-r shadow-lg">
          <div className="container-toys flex items-center justify-between gap-4 px-4 py-2.5">
            <div className="flex min-w-0 items-center gap-3 text-sm font-medium text-white">
              <Icon name="WifiOff" size={18} strokeWidth={2.5} className="shrink-0" />
              <span className="truncate">
                You&rsquo;re offline. Don&rsquo;t worry &mdash; we&rsquo;ll save your session until
                you&rsquo;re back.
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                onClick={handleRetry}
                disabled={isRetrying}
                variant="ghost"
                size="sm"
                className="rounded-lg !px-3 !py-1.5 text-xs text-white backdrop-blur-sm hover:bg-white/30 hover:text-white"
                aria-label={isRetrying ? 'Checking connection…' : 'Retry connection'}>
                <Icon
                  name="RefreshCcw"
                  size={14}
                  strokeWidth={2.5}
                  className={isRetrying ? 'animate-spin' : ''}
                />
                <span className="hidden sm:inline">{isRetrying ? 'Checking…' : 'Retry'}</span>
              </Button>
              <button
                onClick={handleDismiss}
                type="button"
                className="flex items-center justify-center rounded-lg p-1.5 text-white/70 backdrop-blur-sm transition-colors hover:bg-white/20 hover:text-white"
                aria-label="Dismiss offline notice">
                <Icon name="X" size={16} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {isOnline && showOnlineToast && (
        <motion.div
          key="online-toast"
          variants={successVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          role="status"
          aria-live="polite"
          className="from-success-500 to-success-600 dark:from-success-600 dark:to-success-700 sticky top-0 right-0 left-0 z-[9999] bg-gradient-to-r shadow-lg">
          <div className="container-toys flex items-center justify-center gap-2.5 px-4 py-2.5">
            <Icon name="CheckCircle" size={18} strokeWidth={2.5} className="shrink-0 text-white" />
            <span className="text-sm font-medium text-white">
              You&rsquo;re back online! Everything is synced and ready to go.
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OfflineBanner;
